import React from 'react';

import { Box, IconButton, Typography } from '@mui/material';
import type { GridColDef, GridRenderCellParams, GridRowId } from '@mui/x-data-grid-premium';
import {
  gridColumnLookupSelector,
  useGridApiContext,
  useGridSelector,
} from '@mui/x-data-grid-premium';
import { isValid, parse } from 'date-fns';
import type { GroupHeaderRow } from 'src/components';
import { DocumentStatusChip } from 'src/components';
import { ArrowRightIcon } from 'src/components/icons/ArrowRightIcon';
import { ExpandIcon } from 'src/components/icons/ExpandIcon';
import { Apollo } from 'src/gql/generated';
import { formatDateForDisplay, getIsDocumentStatus } from 'src/helpers';
import { getIsRowExpanded } from 'src/hooks/data-grid-premium';

/** Group keys the server emits for rows with no value, kept as-is. */
const EMPTY_GROUP_KEY_PREFIX = '(No ';

/** The calendar-day format the server keys date columns on. */
const GROUP_DATE_KEY_FORMAT = 'yyyy-MM-dd';

/**
 * Renders a group key for display.
 *
 * Date columns arrive as the server's calendar-day key ("2026-03-01"), which
 * is a normalized value rather than a label — parsed as a local date so the day
 * shown matches the day grouped on, instead of shifting west of UTC. Everything
 * else the server already normalizes into something readable.
 */
export function getGroupKeyLabel(groupKey: string, columnType: string | undefined): string {
  const isDateColumn = columnType === 'date' || columnType === 'dateTime';
  if (!isDateColumn || groupKey.startsWith(EMPTY_GROUP_KEY_PREFIX)) return groupKey;

  /**
   * Parsed against the exact format, and local by construction. Appending a
   * time to the key and handing it to `Date` would also accept keys that merely
   * start like a date — "2026-03" parses as the first of the month — and format
   * a text answer as a day the document was never grouped under.
   */
  const localDate = parse(groupKey, GROUP_DATE_KEY_FORMAT, new Date());
  return isValid(localDate) ? formatDateForDisplay(localDate) : groupKey;
}

/**
 * The grouped column's data type, which decides whether the key is a calendar
 * day to format.
 *
 * Selecting the type rather than the column lookup keeps every visible group
 * header out of unrelated column-state updates: the lookup takes a new identity
 * on each resize frame, visibility toggle and reorder, which would re-render
 * all of them and defeat the memo.
 */
function selectGroupColumnType(
  apiRef: Parameters<typeof gridColumnLookupSelector>[0],
  groupFieldId: string
): string | undefined {
  return gridColumnLookupSelector(apiRef)[groupFieldId]?.type;
}

interface GroupHeaderRowCellProps {
  cellParams: GridRenderCellParams<GroupHeaderRow>;
  toggleRowExpansion: (rowId: GridRowId) => void;
  /**
   * The grouped column's type when the grid defines no column for it, so a
   * date question a grid groups by without showing still gets its
   * calendar-day keys formatted. The grid's own column wins when it has one.
   */
  fallbackColumnType?: GridColDef['type'];
}

export const GroupHeaderRowCell = React.memo(function GroupHeaderRowCell({
  cellParams,
  fallbackColumnType,
  toggleRowExpansion,
}: GroupHeaderRowCellProps) {
  const row = cellParams.row;
  const depth = cellParams.rowNode.depth;
  const maybeStatusString = row.groupKey;
  const isExpanded = getIsRowExpanded(cellParams.rowNode);
  const apiRef = useGridApiContext();
  const groupColumnType = useGridSelector(apiRef, selectGroupColumnType, row.groupFieldId);
  /**
   * The status column is the one grouped column with a chip of its own. Keyed
   * off `groupFieldId`, the identifier every column has, rather than the
   * deprecated enum mirror of it.
   */
  const isStatusGroup =
    row.groupFieldId === Apollo.ReportColumnFileField.Status &&
    getIsDocumentStatus(maybeStatusString);
  const groupKeyLabel = getGroupKeyLabel(row.groupKey, groupColumnType ?? fallbackColumnType);

  const handleToggle = (event: React.MouseEvent) => {
    event.stopPropagation();
    toggleRowExpansion(cellParams.id);
  };

  return (
    <Box
      sx={{
        alignItems: 'center',
        display: 'flex',
        gap: 0.5,
        height: '100%',
        pl: depth * 2,
      }}
    >
      <IconButton size="small" onClick={handleToggle}>
        {isExpanded ? <ExpandIcon /> : <ArrowRightIcon />}
      </IconButton>
      {isStatusGroup ? (
        <>
          <DocumentStatusChip status={maybeStatusString} />
          <Typography variant="body2">({row.documentCount})</Typography>
        </>
      ) : (
        <Typography variant="body2">
          {groupKeyLabel} ({row.documentCount})
        </Typography>
      )}
    </Box>
  );
});
